<template>
    <b-modal
        id="feedback-support-modal"
        ref="feedbackSupportModal"
        data-test="modal-feedback-support"
        :data-active-tab="activeTab"
        size="lg"
        hide-footer
        centered
        header-class="border-0 pb-1"
        body-class="pt-1 pb-4 px-4 feedback-modal-body"
    >
        <template #modal-title>
            <div class="d-flex align-items-center">
                <i class="bi bi-question-circle text-primary mr-2 h4 mb-0"></i>
                <span class="font-weight-bold">Help & Feedback</span>
            </div>
        </template>

        <!-- Mode Nav Pills -->
        <div class="feedback-nav-tabs mb-3 d-flex flex-wrap border-bottom pb-2">
            <button
                type="button"
                class="btn btn-sm feedback-tab-btn mr-2 mb-1"
                :class="{ 'active': activeTab === 'overview' }"
                data-test="tab-feedback-overview"
                @click="switchTab('overview')"
            >
                <i class="bi bi-compass text-success mr-1"></i> How It Works
            </button>
            <button
                type="button"
                class="btn btn-sm feedback-tab-btn mr-2 mb-1"
                :class="{ 'active': activeTab === 'qa' }"
                data-test="tab-feedback-qa"
                @click="switchTab('qa')"
            >
                <i class="bi bi-question-diamond-fill text-info mr-1"></i> Q&amp;A
            </button>
            <button
                type="button"
                class="btn btn-sm feedback-tab-btn mr-2 mb-1"
                :class="{ 'active': activeTab === 'feedback' }"
                data-test="tab-feedback-feedback"
                @click="switchTab('feedback')"
            >
                <i class="bi bi-star-fill text-warning mr-1"></i> Give Feedback
            </button>
            <button
                type="button"
                class="btn btn-sm feedback-tab-btn mr-2 mb-1"
                :class="{ 'active': activeTab === 'problem' }"
                data-test="tab-feedback-problem"
                @click="switchTab('problem')"
            >
                <i class="bi bi-bug-fill text-danger mr-1"></i> Report a Problem
            </button>
            <button
                type="button"
                class="btn btn-sm feedback-tab-btn mb-1"
                :class="{ 'active': activeTab === 'help' }"
                data-test="tab-feedback-help"
                @click="switchTab('help')"
            >
                <i class="bi bi-keyboard-fill text-secondary mr-1"></i> Shortcuts
            </button>
        </div>

        <!-- TAB 1: HOW IT WORKS (Application Overview & Core Workflow) -->
        <div v-show="activeTab === 'overview'" class="tab-content-pane">
            <div class="overview-header mb-3">
                <h6 class="font-weight-bold mb-1 text-primary d-flex align-items-center">
                    <i class="bi bi-compass mr-2"></i> Application Overview &amp; Main Features
                </h6>
                <p class="text-muted small mb-0">
                    VelocyTrack is designed to streamline your daily engineering workflow � uniting sprint backlog tracking, time logging, and pull request activity in one place.
                </p>
            </div>

            <b-row class="mb-2">
                <b-col md="6" class="mb-3">
                    <div class="tip-card p-3 rounded border h-100">
                        <div class="d-flex align-items-center font-weight-bold small text-primary mb-2">
                            <span class="module-badge-icon bg-light-primary text-primary mr-2">
                                <i class="bi bi-clock-history"></i>
                            </span>
                            <span>Timelog</span>
                        </div>
                        <p class="small text-muted mb-2">
                            Log spent hours daily against sprint User Stories, child Tasks, or overhead activities (Meetings, Code Reviews).
                        </p>
                        <ul class="small text-muted pl-3 mb-0">
                            <li>Select active sprints to load their backlog hierarchy.</li>
                            <li>Quickly log absences (Vacation, Sick, Holiday) with 1 click.</li>
                            <li>Export logs directly to CSV or Excel for timesheets.</li>
                            <li>Automated email reminders notify you if your logged hours fall behind expected capacity.</li>
                        </ul>
                    </div>
                </b-col>

                <b-col md="6" class="mb-3">
                    <div class="tip-card p-3 rounded border h-100">
                        <div class="d-flex align-items-center font-weight-bold small text-primary mb-2">
                            <span class="module-badge-icon bg-light-success text-success mr-2">
                                <i class="bi bi-list-check"></i>
                            </span>
                            <span>Backlog</span>
                        </div>
                        <p class="small text-muted mb-2">
                            Your interactive sprint breakdown board synchronized directly with your platform connection.
                        </p>
                        <ul class="small text-muted pl-3 mb-0">
                            <li>Manage task statuses (To Do, In Progress, Done) & remaining hours.</li>
                            <li>Use <strong>Toggle Collapse ALL</strong> to condense child items.</li>
                            <li>Log work directly on tasks without navigating away.</li>
                        </ul>
                    </div>
                </b-col>

                <b-col md="6" class="mb-3">
                    <div class="tip-card p-3 rounded border h-100">
                        <div class="d-flex align-items-center font-weight-bold small text-primary mb-2">
                            <span class="module-badge-icon bg-light-info text-info mr-2">
                                <i class="bi bi-git"></i>
                            </span>
                            <span>Pull Requests</span>
                        </div>
                        <p class="small text-muted mb-2">
                            Track the review lifecycle of all Pull Requests associated with your active sprint items.
                        </p>
                        <ul class="small text-muted pl-3 mb-0">
                            <li>View reviewer approvals, pending votes, and merge status.</li>
                            <li>Ensure code review progress stays on track with sprint deadlines.</li>
                        </ul>
                    </div>
                </b-col>

                <b-col md="6" class="mb-3">
                    <div class="tip-card p-3 rounded border h-100">
                        <div class="d-flex align-items-center font-weight-bold small text-primary mb-2">
                            <span class="module-badge-icon bg-light-warning text-warning mr-2">
                                <i class="bi bi-sliders"></i>
                            </span>
                            <span>Filters &amp; Display Scaling</span>
                        </div>
                        <p class="small text-muted mb-2">
                            Personalize your daily workspace for maximum efficiency and readability.
                        </p>
                        <ul class="small text-muted pl-3 mb-0">
                            <li>Save persistent defaults (e.g. "Assigned to Me") via server-side filters.</li>
                            <li>Compact table grids for laptops using the <strong>Zoom</strong> dropdown (80%/90%).</li>
                            <li>Manage tokens and project settings in Configuration.</li>
                        </ul>
                    </div>
                </b-col>
            </b-row>

            <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top flex-wrap">
                <div class="small text-muted mb-2 mb-sm-0">
                    <i class="bi bi-shield-check mr-1 text-success"></i>
                    <span>Legal &amp; Policies:</span>
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('terms')" data-test="link-help-tos">Terms of Service</a> &bull;
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('privacy')" data-test="link-help-privacy">Privacy Policy</a> &bull;
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('security')" data-test="link-help-security">Security</a>
                </div>
                <b-button variant="outline-secondary" size="sm" class="px-4" @click="closeModal">
                    <i class="bi bi-check2 mr-1"></i>Got it
                </b-button>
            </div>
        </div>

        <!-- TAB 2: Q&A (Common Pitfalls & Points of Interest) -->
        <div v-show="activeTab === 'qa'" class="tab-content-pane">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div>
                    <h6 class="font-weight-bold mb-1 text-primary d-flex align-items-center">
                        <i class="bi bi-question-diamond-fill mr-2"></i> Frequently Asked Questions &amp; Pitfalls
                    </h6>
                    <p class="text-muted small mb-0">
                        Quick solutions for common questions, filters, and hidden toggles.
                    </p>
                </div>
                <div class="mt-2 mt-sm-0" style="max-width: 240px; width: 100%;">
                    <b-input-group size="sm">
                        <b-form-input
                            v-model="qaSearchQuery"
                            placeholder="Filter questions..."
                            class="feedback-input"
                        ></b-form-input>
                        <b-input-group-append v-if="qaSearchQuery">
                            <b-button variant="outline-secondary" size="sm" @click="qaSearchQuery = ''">
                                <i class="bi bi-x"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </div>

            <div class="qa-items-container" style="max-height: 440px; overflow-y: auto; padding-right: 4px;">
                <div v-if="filteredQaList.length === 0" class="text-center py-4 text-muted small">
                    <i class="bi bi-search h4 d-block text-muted mb-2"></i>
                    No questions found matching "{{ qaSearchQuery }}".
                </div>

                <div
                    v-for="(item, idx) in filteredQaList"
                    :key="idx"
                    class="qa-card p-3 rounded border mb-2"
                >
                    <div class="d-flex justify-content-between align-items-start mb-1">
                        <div class="font-weight-bold small text-primary d-flex align-items-center">
                            <i class="bi bi-question-circle-fill mr-2 text-info"></i>
                            <span>{{ item.q }}</span>
                        </div>
                        <span class="badge badge-light ml-2 text-nowrap">{{ item.category }}</span>
                    </div>
                    <p class="small text-muted mb-0 pl-4" style="line-height: 1.55;">
                        {{ item.a }}
                    </p>
                </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                <small class="text-muted">
                    Need more help? Use <strong>Report a Problem</strong> or <strong>Give Feedback</strong>.
                </small>
                <b-button variant="outline-secondary" size="sm" class="px-4" @click="closeModal">
                    <i class="bi bi-check2 mr-1"></i>Got it
                </b-button>
            </div>
        </div>

        <!-- TAB 3: GIVE FEEDBACK -->
        <div v-show="activeTab === 'feedback'" class="tab-content-pane">
            <!-- Limit Reached State: Form controls hidden -->
            <div v-if="!quota.canSubmitFeedback" class="limit-reached-card text-center py-4 px-3 rounded border my-2" data-test="state-feedback-limit">
                <div class="mb-3">
                    <span class="limit-reached-icon-circle bg-light-info text-info">
                        <i class="bi bi-chat-heart-fill"></i>
                    </span>
                </div>
                <h5 class="font-weight-bold mb-2">Daily Feedback Submitted</h5>
                <p class="text-muted small mx-auto mb-4" style="max-width: 480px; line-height: 1.6;">
                    {{ quota.feedbackMessage || "Thanks for keeping us in the loop! You've already submitted feedback for today. Please hold on and check back tomorrow 🙏" }}
                </p>
                <b-button variant="outline-secondary" size="sm" class="px-4" @click="closeModal">
                    <i class="bi bi-check2 mr-1"></i>Got it
                </b-button>
            </div>

            <!-- Active Feedback Form (Hidden when limit reached) -->
            <div v-else>
                <p class="text-muted small mb-3">
                    How has your experience with VelocyTrack been? Your ratings and feedback directly shape upcoming updates.
                </p>

            <!-- Overall Star Rating Widget -->
            <div class="star-rating-box mb-3 text-center py-3 rounded">
                <div class="small font-weight-bold text-muted text-uppercase mb-1" style="letter-spacing: 0.05em;">
                    Overall Experience <span class="text-danger">*</span>
                </div>
                <div class="stars-row mb-1">
                    <button
                        v-for="star in 5"
                        :key="star"
                        type="button"
                        class="star-btn"
                        :class="{ 'active': (hoverRating || rating) >= star }"
                        @mouseenter="hoverRating = star"
                        @mouseleave="hoverRating = 0"
                        @click="setRating(star)"
                        :title="'Rate ' + star + ' star' + (star > 1 ? 's' : '')"
                        :aria-label="'Rate ' + star + ' star' + (star > 1 ? 's' : '')"
                        :data-test="`btn-star-${star}`"
                    >
                        <i :class="(hoverRating || rating) >= star ? 'bi bi-star-fill' : 'bi bi-star'"></i>
                    </button>
                </div>
                <div class="rating-label font-weight-bold" :class="rating ? 'text-warning' : 'text-muted'">
                    {{ ratingLabel }}
                </div>
            </div>

            <!-- Optional Category Breakdown with Mini Stars -->
            <div class="category-ratings-section mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <label class="font-weight-bold small text-muted mb-0">Category Breakdown (Optional)</label>
                    <small class="text-muted">Rate specific areas to guide our focus</small>
                </div>
                <div class="category-ratings-list rounded border p-2">
                    <div
                        v-for="cat in ratingCategories"
                        :key="cat.key"
                        class="category-rating-row d-flex justify-content-between align-items-center py-1 px-2"
                    >
                        <div class="small font-weight-500 text-truncate mr-2">
                            <i :class="cat.icon" class="mr-2 text-muted"></i>
                            <span>{{ cat.label }}</span>
                        </div>
                        <div class="d-flex align-items-center">
                            <div class="mini-stars-row mr-2">
                                <button
                                    v-for="star in 5"
                                    :key="star"
                                    type="button"
                                    class="mini-star-btn"
                                    :class="{ 'active': (hoverCategoryRatings[cat.key] || categoryRatings[cat.key]) >= star }"
                                    @mouseenter="$set(hoverCategoryRatings, cat.key, star)"
                                    @mouseleave="$set(hoverCategoryRatings, cat.key, 0)"
                                    @click="toggleCategoryRating(cat.key, star)"
                                    :title="cat.label + ': ' + star + ' stars'"
                                >
                                    <i :class="(hoverCategoryRatings[cat.key] || categoryRatings[cat.key]) >= star ? 'bi bi-star-fill' : 'bi bi-star'"></i>
                                </button>
                            </div>
                            <span v-if="categoryRatings[cat.key]" class="mini-rating-value badge badge-light font-weight-bold">
                                {{ categoryRatings[cat.key] }}/5
                            </span>
                            <button
                                v-if="categoryRatings[cat.key]"
                                type="button"
                                class="btn btn-link btn-sm text-muted p-0 ml-1 reset-cat-btn"
                                @click="clearCategoryRating(cat.key)"
                                title="Clear rating"
                            >
                                <i class="bi bi-x"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Comments Textarea -->
            <div class="form-group mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <label class="font-weight-bold small text-muted mb-0">Your Thoughts & Suggestions</label>
                    <small class="text-muted">{{ feedbackText.length }} / 2000</small>
                </div>
                <b-form-textarea
                    v-model="feedbackText"
                    placeholder="Tell us what you like or what could be improved..."
                    rows="3"
                    max-rows="5"
                    maxlength="2000"
                    class="feedback-textarea"
                    data-test="input-feedback-text"
                ></b-form-textarea>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-content-end align-items-center mt-3">
                <b-button variant="secondary" class="mr-2" @click="closeModal" :disabled="isSubmitting">
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    @click="submitRatingFeedback"
                    :disabled="rating === 0 || isSubmitting"
                    class="px-4"
                    data-test="btn-submit-feedback"
                    :data-state="isSubmitting ? 'loading' : 'idle'"
                >
                    <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                    <i v-else class="bi bi-send-fill mr-1"></i>
                    Send Feedback
                </b-button>
            </div>
            </div>
        </div>

        <!-- TAB 2: REPORT A PROBLEM -->
        <div v-show="activeTab === 'problem'" class="tab-content-pane">
            <!-- Limit Reached State: Form controls hidden -->
            <div v-if="!isUnlimitedProblem && !quota.canSubmitProblem" class="limit-reached-card text-center py-4 px-3 rounded border my-2" data-test="state-problem-limit">
                <div class="mb-3">
                    <span class="limit-reached-icon-circle bg-light-warning text-warning">
                        <i class="bi bi-shield-check"></i>
                    </span>
                </div>
                <h5 class="font-weight-bold mb-2">Daily Problem Report Limit Reached ({{ quota.problemDailyLimit }}/{{ quota.problemDailyLimit }})</h5>
                <p class="text-muted small mx-auto mb-4" style="max-width: 480px; line-height: 1.6;">
                    {{ quota.problemMessage || ("You've submitted " + quota.problemDailyLimit + " problem reports today. Our team is already looking into things. Please hold on and check back tomorrow 🙏") }}
                </p>
                <b-button variant="outline-secondary" size="sm" class="px-4" @click="closeModal">
                    <i class="bi bi-check2 mr-1"></i>Got it
                </b-button>
            </div>

            <!-- Active Problem Form (Hidden when limit reached) -->
            <div v-else>
                <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <p class="text-muted small mb-0 mr-2">
                        Found a bug, visual glitch, or sync discrepancy? Report it below with reproduction steps so we can resolve it promptly.
                    </p>
                    <span v-if="isUnlimitedProblem" class="badge badge-success py-1 px-2 text-nowrap" data-test="badge-unlimited-beta-reports">
                        <i class="bi bi-infinity mr-1"></i>Unlimited Beta Reports ({{ quota.problemTodayCount }} today)
                    </span>
                    <span v-else class="badge badge-secondary py-1 px-2 text-nowrap" data-test="badge-problem-quota">
                        {{ quota.problemTodayCount }}/{{ quota.problemDailyLimit }} reports today
                    </span>
                </div>

            <!-- Issue Category -->
            <div class="form-group mb-3">
                <label class="font-weight-bold small text-muted mb-2">Issue Type</label>
                <div class="chips-container d-flex flex-wrap">
                    <button
                        v-for="cat in problemCategories"
                        :key="cat"
                        type="button"
                        class="badge badge-pill chip-btn mr-2 mb-2"
                        :class="problemCategory === cat ? 'chip-active-problem' : 'chip-inactive'"
                        data-test="chip-problem-category"
                        :data-test-id="cat"
                        @click="problemCategory = cat"
                    >
                        {{ cat }}
                    </button>
                </div>
            </div>

            <!-- Issue Title -->
            <div class="form-group mb-3">
                <label class="font-weight-bold small text-muted mb-1">Summary / Title <span class="text-danger">*</span></label>
                <b-form-input
                    v-model="problemTitle"
                    placeholder="e.g. Sprint picker shows duplicate dates after switching projects"
                    class="feedback-input"
                    maxlength="300"
                    data-test="input-problem-title"
                ></b-form-input>
            </div>

            <!-- Description / Reproduction Steps -->
            <div class="form-group mb-3">
                <label class="font-weight-bold small text-muted mb-1">Description & Steps to Reproduce <span class="text-danger">*</span></label>
                <b-form-textarea
                    v-model="problemDescription"
                    placeholder="1. Go to page...&#10;2. Click on...&#10;3. Observed behavior vs expected behavior..."
                    rows="4"
                    max-rows="6"
                    class="feedback-textarea"
                    data-test="input-problem-description"
                ></b-form-textarea>
            </div>

            <!-- Screenshots / Attachments (Up to 3, max 2MB each) -->
            <div class="form-group mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <label class="font-weight-bold small text-muted mb-0">
                        Screenshots / Attachments (Optional)
                    </label>
                    <span class="badge" :class="attachments.length >= maxAttachments ? 'badge-warning' : 'badge-light'">
                        {{ attachments.length }} / {{ maxAttachments }} images (max 2 MB each)
                    </span>
                </div>

                <!-- Dropzone (shown if less than 3 attachments) -->
                <div
                    v-if="attachments.length < maxAttachments"
                    class="attachment-dropzone p-3 text-center rounded border mb-2"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleFileDrop"
                    @paste="handlePaste"
                    :class="{ 'dropzone-active': isDragging }"
                    tabindex="0"
                >
                    <div class="py-1">
                        <i class="bi bi-image text-muted h3 mb-1 d-block"></i>
                        <div class="small text-muted mb-2">
                            Paste screenshot (<kbd>Ctrl</kbd> + <kbd>V</kbd>) or drag & drop image here
                        </div>
                        <b-button size="sm" variant="outline-primary" @click="triggerFileInput">
                            <i class="bi bi-paperclip mr-1"></i> Browse Images
                        </b-button>
                        <input
                            type="file"
                            ref="fileInput"
                            accept="image/*"
                            multiple
                            class="d-none"
                            @change="handleFileSelect"
                        />
                    </div>
                </div>

                <!-- Thumbnails List -->
                <div v-if="attachments.length > 0" class="attachments-list">
                    <b-row>
                        <b-col
                            v-for="(att, idx) in attachments"
                            :key="att.id"
                            sm="6"
                            md="4"
                            class="mb-2"
                        >
                            <div class="attachment-card p-2 rounded border d-flex align-items-center justify-content-between position-relative">
                                <div class="d-flex align-items-center text-truncate mr-2">
                                    <img :src="att.base64" alt="Screenshot" class="preview-thumbnail rounded mr-2" />
                                    <div class="text-truncate">
                                        <div class="small font-weight-bold text-truncate" :title="att.name">
                                            {{ att.name }}
                                        </div>
                                        <div class="small text-muted">{{ formatSize(att.size) }}</div>
                                    </div>
                                </div>
                                <b-button
                                    size="sm"
                                    variant="outline-danger"
                                    class="py-0 px-1 border-0"
                                    @click="removeAttachment(idx)"
                                    title="Remove this image"
                                >
                                    <i class="bi bi-x-circle-fill"></i>
                                </b-button>
                            </div>
                        </b-col>
                    </b-row>
                </div>
            </div>

            <!-- Automated Diagnostic Telemetry Card -->
            <div class="diagnostic-card mb-3 rounded border">
                <div
                    class="diagnostic-header px-3 py-2 d-flex justify-content-between align-items-center cursor-pointer"
                    @click="isDiagExpanded = !isDiagExpanded"
                >
                    <div class="d-flex align-items-center small font-weight-bold">
                        <i class="bi bi-cpu text-info mr-2"></i>
                        <span>Automated Diagnostic Context</span>
                        <span class="badge badge-info ml-2 px-2 py-0.5">Auto-Captured</span>
                    </div>
                    <div class="d-flex align-items-center">
                        <small class="text-muted mr-2">{{ isDiagExpanded ? 'Hide' : 'Inspect' }}</small>
                        <i :class="isDiagExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="text-muted small"></i>
                    </div>
                </div>
                <div v-show="isDiagExpanded" class="diagnostic-body p-3 border-top">
                    <b-row class="small">
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">Route:</strong>
                            <code class="ml-1">{{ diagnosticContext.route }}</code>
                        </b-col>
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">Theme:</strong>
                            <span class="badge badge-secondary ml-1">{{ diagnosticContext.theme }}</span>
                        </b-col>
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">Browser:</strong>
                            <span class="ml-1">{{ diagnosticContext.browser }}</span>
                        </b-col>
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">Resolution:</strong>
                            <span class="ml-1">{{ diagnosticContext.resolution }}</span>
                        </b-col>
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">Zoom Density:</strong>
                            <span class="ml-1">{{ diagnosticContext.zoom }}</span>
                        </b-col>
                        <b-col sm="6" class="mb-2">
                            <strong class="text-muted">User:</strong>
                            <span class="ml-1 text-truncate d-inline-block" style="max-width: 180px;">{{ diagnosticContext.email || 'Anonymous' }}</span>
                        </b-col>
                    </b-row>
                    <div class="custom-control custom-checkbox mt-2">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="includeTelemetryCheck"
                            v-model="includeTelemetry"
                            data-test="chk-include-telemetry"
                        />
                        <label class="custom-control-label small text-muted" for="includeTelemetryCheck">
                            Include this diagnostic telemetry with your report to assist troubleshooting
                        </label>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-content-end align-items-center mt-4">
                <b-button variant="secondary" class="mr-2" @click="closeModal" :disabled="isSubmitting">
                    Cancel
                </b-button>
                <b-button
                    variant="danger"
                    @click="submitProblemReport"
                    :disabled="!problemTitle.trim() || !problemDescription.trim() || isSubmitting"
                    class="px-4"
                    data-test="btn-submit-problem"
                    :data-state="isSubmitting ? 'loading' : 'idle'"
                >
                    <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                    <i v-else class="bi bi-exclamation-triangle-fill mr-1"></i>
                    Submit Problem Report
                </b-button>
            </div>
            </div>
        </div>

        <!-- TAB 3: KEYBOARD SHORTCUTS & QUICK TIPS -->
        <div v-show="activeTab === 'help'" class="tab-content-pane">
            <div class="shortcuts-section mb-4">
                <h6 class="font-weight-bold mb-3 d-flex align-items-center">
                    <i class="bi bi-command mr-2 text-primary"></i> Keyboard Shortcuts
                </h6>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered shortcut-table mb-0">
                        <thead>
                            <tr>
                                <th style="width: 220px;">Shortcut</th>
                                <th>Action</th>
                                <th style="width: 130px;">Scope</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><kbd>?</kbd> or <kbd>Shift</kbd> + <kbd>/</kbd></td>
                                <td>Open Help & Feedback Dialog</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>Alt</kbd> + <kbd>T</kbd></td>
                                <td>Navigate to Timelog</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>Alt</kbd> + <kbd>D</kbd></td>
                                <td>Navigate to Discussions</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>Alt</kbd> + <kbd>K</kbd></td>
                                <td>Navigate to Backlog</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>Alt</kbd> + <kbd>P</kbd></td>
                                <td>Navigate to Pull Requests</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>Alt</kbd> + <kbd>C</kbd></td>
                                <td>Navigate to Configuration</td>
                                <td><span class="badge badge-light">Global</span></td>
                            </tr>
                            <tr>
                                <td><kbd>W</kbd> or <kbd>Alt</kbd> + <kbd>W</kbd></td>
                                <td>Add Work Item</td>
                                <td><span class="badge badge-info">Timelog</span></td>
                            </tr>
                            <tr>
                                <td><kbd>A</kbd> or <kbd>Alt</kbd> + <kbd>A</kbd></td>
                                <td>Add Activity</td>
                                <td><span class="badge badge-info">Timelog</span></td>
                            </tr>
                            <tr>
                                <td><kbd>B</kbd> or <kbd>Alt</kbd> + <kbd>B</kbd></td>
                                <td>Add Absence</td>
                                <td><span class="badge badge-info">Timelog</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="small text-muted mt-2">
                    <i class="bi bi-info-circle mr-1"></i> Shortcuts work across international and Cyrillic keyboard layouts when not typing inside an input or text field.
                </div>
            </div>


            <!-- Footer Close & Policies -->
            <div class="d-flex justify-content-between align-items-center mt-4 pt-2 border-top flex-wrap">
                <div class="small text-muted mb-2 mb-sm-0">
                    <i class="bi bi-shield-check mr-1 text-success"></i>
                    <span>Legal:</span>
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('terms')" data-test="link-shortcuts-tos">Terms</a> &bull;
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('privacy')" data-test="link-shortcuts-privacy">Privacy</a> &bull;
                    <a href="#" class="text-primary ml-1" @click.prevent="openLegal('security')" data-test="link-shortcuts-security">Acceptable Use</a>
                </div>
                <b-button variant="outline-secondary" size="sm" class="px-4" @click="closeModal">
                    <i class="bi bi-check2 mr-1"></i>Got it
                </b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
    import eventBus from '../event-bus';
    import toastMixin from '../mixinToast';
    import accountMixin from '../mixinAccount';

    const MAX_ATTACHMENTS = 3;
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB per image

    export default {
        name: 'ModalFeedbackSupport',
        mixins: [toastMixin, accountMixin],
        data() {
            return {
                activeTab: 'overview', // 'overview' | 'qa' | 'feedback' | 'problem' | 'help'
                isSubmitting: false,
                maxAttachments: MAX_ATTACHMENTS,

                // Tab 2: Q&A Search and Items
                qaSearchQuery: '',
                qaList: [
                    {
                        category: 'Backlog',
                        q: 'Why do tasks or child items appear to be missing in Backlog?',
                        a: 'If "Toggle Collapse ALL" is active, child tasks and item details are tucked under their parent User Story cards. Click "Toggle Collapse ALL" in the table header (or the expand chevron on individual cards) to reveal them. Since this state is remembered across sessions, check this toggle first if the board looks empty!'
                    },
                    {
                        category: 'Sprints & Dates',
                        q: 'The sprint picker displays unexpected dates or old sprints.',
                        a: 'Sprints sync dynamically from your configured platform connection board. Ensure the correct Project and Team Area are selected. Switching projects or re-selecting a sprint refreshes the date boundaries.'
                    },
                    {
                        category: 'Backlog & Filters',
                        q: 'Why can\'t I see tasks assigned to other team members?',
                        a: 'Views default to filtering by "Assigned to Me". Open the Assignee filter dropdown and clear or expand your selection to view items across the entire team.'
                    },
                    {
                        category: 'Integration',
                        q: 'Work items or pull requests from platform connection are not syncing.',
                        a: 'Navigate to Configuration (gear icon in the top navbar) and verify that your platform connection URL, Project, and Personal Access Token (PAT) are active and have sufficient read permissions.'
                    },
                    {
                        category: 'Display & Zoom',
                        q: 'The tables feel cramped or don\'t fit comfortably on my laptop screen.',
                        a: 'Use the Zoom dropdown in the top navbar to set scaling to 80% or 90%. This compacts row padding and font scaling so full weekly grids fit neatly on smaller displays.'
                    },
                    {
                        category: 'Reporting & Export',
                        q: 'How do I export my logged hours for timesheets or invoices?',
                        a: 'In Timelog, click the "Export" button on the toolbar. This generates a formatted report of all logged hours for the selected sprint and filters.'
                    },
                    {
                        category: 'Shortcuts',
                        q: 'Can I navigate and perform actions without using the mouse?',
                        a: 'Yes! Press "?" to view the Shortcuts tab. Global shortcuts like Alt+T (Timelog), Alt+D (Discussions), Alt+K (Backlog), Alt+P (Pull Requests), and Alt+C (Configuration) work across all pages and keyboard layouts.'
                    },
                    {
                        category: 'Custom Fields',
                        q: 'How do I add or view custom work item fields in the grid?',
                        a: 'Click the "Customize Columns" button (slider icon) in the grid header. You can discover available process fields from your backlog platform, toggle visibility on/off, drag to reorder columns, and set custom metric labels (such as "pts" or "hours").'
                    },
                    {
                        category: 'Time Tracking',
                        q: 'How should I log blocked time, idle time, or overhead activities?',
                        a: 'Use the "Add Activity" button in Timelog to record overhead (Meetings, Code Reviews, Architecture, or Idle/Blocked). This ensures your daily logged capacity stays accurate without creating unnecessary dummy work items in your backlog.'
                    },
                    {
                        category: 'Notifications',
                        q: 'How do automated email reminders work?',
                        a: 'You can configure automated time log notifications in your Profile under "Time Log Reminders". Set your daily/weekly cutoff time and behind-hours threshold (e.g. 4h or 8h) to receive alert emails with direct links to catch up on unlogged dates.'
                    },
                    {
                        category: 'Privacy & Security',
                        q: 'Does VelocyTrack clone my source code or train AI models on it?',
                        a: 'No. VelocyTrack never clones, mirrors, or permanently stores your repository source code on our servers. Diffs and analytics are processed transiently in memory, and we never train AI or ML models on your private code, commit histories, or task descriptions. You can review our full Terms of Service and Privacy Policy anytime.'
                    }
                ],

                // Tab 3: Overall Rating & Optional Category Breakdown
                rating: 0,
                hoverRating: 0,
                ratingCategories: [
                    { key: 'timeTracking', label: 'Timelog', icon: 'bi bi-clock-history' },
                    { key: 'taskManager', label: 'Backlog', icon: 'bi bi-list-check' },
                    { key: 'prManager', label: 'Pull Requests', icon: 'icon-azure-pr' },
                    { key: 'performance', label: 'Performance & Speed', icon: 'bi bi-speedometer' },
                    { key: 'darkTheme', label: 'Dark Theme & UI Polish', icon: 'bi bi-moon-stars' }
                ],
                categoryRatings: {
                    timeTracking: 0,
                    taskManager: 0,
                    prManager: 0,
                    performance: 0,
                    darkTheme: 0
                },
                hoverCategoryRatings: {
                    timeTracking: 0,
                    taskManager: 0,
                    prManager: 0,
                    performance: 0,
                    darkTheme: 0
                },
                feedbackText: '',

                // Daily Quota Tracking
                quota: {
                    feedbackTodayCount: 0,
                    feedbackDailyLimit: 1,
                    canSubmitFeedback: true,
                    problemTodayCount: 0,
                    problemDailyLimit: 5,
                    canSubmitProblem: true,
                    isUnlimitedProblem: false,
                    feedbackMessage: '',
                    problemMessage: ''
                },
                isLoadingQuota: false,

                // Tab 4: Problem Report
                problemCategory: 'Bug / Error',
                problemCategories: [
                    'Bug / Error',
                    'UI Defect',
                    'Calculation / Data Discrepancy',
                    'Performance Slowness',
                    'Integration / Sync',
                    'Other'
                ],
                problemTitle: '',
                problemDescription: '',
                attachments: [], // Array of { id, name, size, base64 }
                isDragging: false,
                isDiagExpanded: false,
                includeTelemetry: true,

                // Telemetry Context
                diagnosticContext: {
                    route: '',
                    theme: 'dark',
                    browser: '',
                    resolution: '',
                    zoom: '100%',
                    email: '',
                    timestamp: ''
                }
            };
        },
        computed: {
            ratingLabel() {
                const r = this.hoverRating || this.rating;
                switch (r) {
                    case 1: return '1 - Poor';
                    case 2: return '2 - Fair';
                    case 3: return '3 - Good';
                    case 4: return '4 - Very Good';
                    case 5: return '5 - Excellent!';
                    default: return 'Click a star to rate overall';
                }
            },
            filteredQaList() {
                if (!this.qaSearchQuery || !this.qaSearchQuery.trim()) {
                    return this.qaList;
                }
                const q = this.qaSearchQuery.trim().toLowerCase();
                return this.qaList.filter(item =>
                    item.q.toLowerCase().includes(q) ||
                    item.a.toLowerCase().includes(q) ||
                    item.category.toLowerCase().includes(q)
                );
            },
            isUnlimitedProblem() {
                if (this.currentEnvironment === 'production') {
                    return false;
                }
                return !!this.quota.isUnlimitedProblem || this.currentEnvironment === 'beta' || this.currentEnvironment === 'development';
            }
        },
        watch: {
            currentEnvironment() {
                this.fetchQuotaStatus();
            }
        },
        mounted() {
            this.handleOpenFeedbackModal = (tab, prefill) => this.openModal(tab, prefill);
            eventBus.$on('open-feedback-modal', this.handleOpenFeedbackModal);
            eventBus.$on('open-feedback-support', this.handleOpenFeedbackModal);
            window.addEventListener('paste', this.handleGlobalPaste);
        },
        beforeUnmount() {
            if (this.handleOpenFeedbackModal) {
                eventBus.$off('open-feedback-modal', this.handleOpenFeedbackModal);
                eventBus.$off('open-feedback-support', this.handleOpenFeedbackModal);
            }
            window.removeEventListener('paste', this.handleGlobalPaste);
        },
        methods: {
            openModal(tab = 'overview', prefill = null) {
                this.activeTab = ['overview', 'qa', 'feedback', 'problem', 'help'].includes(tab) ? tab : 'overview';
                if (prefill) {
                    if (prefill.title) this.problemTitle = prefill.title;
                    if (prefill.description) this.problemDescription = prefill.description;
                    if (prefill.category) this.problemCategory = prefill.category;
                }
                this.refreshDiagnosticContext();
                this.fetchQuotaStatus();
                this.$refs.feedbackSupportModal?.show();
            },
            closeModal() {
                this.$refs.feedbackSupportModal?.hide();
            },
            openLegal(tab = 'terms') {
                this.closeModal();
                this.$nextTick(() => {
                    eventBus.$emit('open-legal', tab);
                });
            },
            switchTab(tab) {
                this.activeTab = tab;
                if (tab === 'problem') {
                    this.refreshDiagnosticContext();
                }
                this.fetchQuotaStatus();
            },
            async fetchQuotaStatus() {
                this.isLoadingQuota = true;
                try {
                    const config = (this.isActualDevHost && this.currentEnvironment === 'production')
                        ? { params: { env: 'production' } }
                        : undefined;
                    const resp = await this.$http.get('/Feedback/QuotaStatus', config);
                    if (resp.data) {
                        this.quota = {
                            ...resp.data,
                            feedbackMessage: (resp.data.feedbackMessage || '').replace(/:\)/g, '😊'),
                            problemMessage: (resp.data.problemMessage || '').replace(/:\)/g, '😊')
                        };
                    }
                } catch (e) {
                    console.warn('Could not fetch feedback quota status:', e);
                } finally {
                    this.isLoadingQuota = false;
                }
            },
            setRating(val) {
                this.rating = val;
            },
            toggleCategoryRating(catKey, star) {
                // If user clicks the same rating, toggle it off (reset to 0)
                if (this.categoryRatings[catKey] === star) {
                    this.$set(this.categoryRatings, catKey, 0);
                } else {
                    this.$set(this.categoryRatings, catKey, star);
                }
            },
            clearCategoryRating(catKey) {
                this.$set(this.categoryRatings, catKey, 0);
                this.$set(this.hoverCategoryRatings, catKey, 0);
            },
            formatSize(bytes) {
                if (!bytes) return '0 KB';
                const kb = Math.round(bytes / 1024);
                if (kb < 1000) return `${kb} KB`;
                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
            },
            refreshDiagnosticContext() {
                const ua = navigator.userAgent;
                let browser = 'Unknown Browser';
                if (ua.includes('Edg/')) browser = 'Microsoft Edge';
                else if (ua.includes('Chrome/')) browser = 'Google Chrome';
                else if (ua.includes('Firefox/')) browser = 'Mozilla Firefox';
                else if (ua.includes('Safari/')) browser = 'Apple Safari';

                const theme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark';
                const domZoom = (typeof document !== 'undefined' && document.documentElement.style.zoom)
                    ? `${Math.round(parseFloat(document.documentElement.style.zoom) * 100)}%`
                    : null;
                const currentZoom = localStorage.getItem('vt_ui_zoom') ? `${localStorage.getItem('vt_ui_zoom')}%` : (domZoom || '100%');
                const userEmail = this.$store?.state?.account?.email || '';

                this.diagnosticContext = {
                    route: this.$route ? this.$route.fullPath : '/',
                    theme: theme,
                    browser: browser,
                    resolution: `${window.screen.width}x${window.screen.height}`,
                    zoom: currentZoom,
                    email: userEmail,
                    timestamp: new Date().toISOString()
                };
            },
            triggerFileInput() {
                this.$refs.fileInput?.click();
            },
            handleFileSelect(e) {
                const files = e.target?.files;
                if (files && files.length > 0) {
                    this.addFiles(Array.from(files));
                }
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            },
            handleFileDrop(e) {
                this.isDragging = false;
                const files = e.dataTransfer?.files;
                if (files && files.length > 0) {
                    const imgFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
                    if (imgFiles.length > 0) {
                        this.addFiles(imgFiles);
                    }
                }
            },
            handlePaste(e) {
                const items = e.clipboardData?.items;
                if (!items) return;
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.startsWith('image/')) {
                        const file = items[i].getAsFile();
                        if (file) {
                            this.addFiles([file]);
                            e.preventDefault();
                            break;
                        }
                    }
                }
            },
            handleGlobalPaste(e) {
                // If problem tab is active and modal is open, catch image pastes globally
                if (this.activeTab === 'problem' && this.attachments.length < MAX_ATTACHMENTS) {
                    const items = e.clipboardData?.items;
                    if (!items) return;
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].type.startsWith('image/')) {
                            const file = items[i].getAsFile();
                            if (file) {
                                this.addFiles([file]);
                                e.preventDefault();
                                break;
                            }
                        }
                    }
                }
            },
            addFiles(fileList) {
                for (const file of fileList) {
                    if (this.attachments.length >= MAX_ATTACHMENTS) {
                        this.toastWarning('Maximum 3 Images', 'You can attach up to 3 images in total.');
                        break;
                    }
                    if (file.size > MAX_FILE_SIZE) {
                        const mb = (file.size / (1024 * 1024)).toFixed(1);
                        this.toastError('File Too Large', `"${file.name}" is ${mb} MB. Maximum allowed is 2 MB per image.`);
                        continue;
                    }
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.attachments.push({
                            id: Date.now() + '_' + Math.random().toString(36).substr(2, 6),
                            name: file.name || `Screenshot-${this.attachments.length + 1}.png`,
                            size: file.size,
                            base64: e.target.result
                        });
                    };
                    reader.readAsDataURL(file);
                }
            },
            removeAttachment(index) {
                this.attachments.splice(index, 1);
            },
            async submitRatingFeedback() {
                if (!this.quota.canSubmitFeedback) {
                    this.toastWarning('Daily Limit', this.quota.feedbackMessage || "You've already submitted feedback for today. Please hold on 🙏", 8);
                    return;
                }
                if (this.rating === 0 || this.isSubmitting) return;
                this.isSubmitting = true;

                try {
                    let desc = this.feedbackText.trim();
                    const ratedCats = Object.entries(this.categoryRatings).filter(([, v]) => v > 0);
                    if (ratedCats.length > 0) {
                        const breakdown = ratedCats
                            .map(([k, v]) => {
                                const catObj = this.ratingCategories.find(c => c.key === k);
                                return `� ${catObj ? catObj.label : k}: ${v}/5 stars`;
                            })
                            .join('\n');

                        desc = desc
                            ? `${desc}\n\n[Category Breakdown]\n${breakdown}`
                            : `[Category Breakdown]\n${breakdown}`;
                    }

                    const payload = {
                        type: 'Rating',
                        starRating: this.rating,
                        category: 'Overall Experience',
                        title: `Rating: ${this.rating} Stars (Overall)`,
                        description: desc || '(No written comments)',
                        diagnosticContextJson: JSON.stringify({
                            ...this.diagnosticContext,
                            categoryRatings: this.categoryRatings
                        })
                    };

                    await this.$http.post('/Feedback', payload);
                    this.toastSpecialThanks('feedback');
                    await this.fetchQuotaStatus();

                    // Reset
                    this.rating = 0;
                    this.hoverRating = 0;
                    this.categoryRatings = {
                        timeTracking: 0,
                        taskManager: 0,
                        prManager: 0,
                        performance: 0,
                        darkTheme: 0
                    };
                    this.feedbackText = '';
                    this.closeModal();
                } catch (err) {
                    if (err.status === 429) {
                        const rawMsg = err.message || "Thanks for keeping us in the loop! You've already submitted feedback for today. Please hold on and check back tomorrow 🙏";
                        const polite = rawMsg.replace(/:\)/g, '😊');
                        this.toastWarning('Daily Limit', polite, 10);
                        this.fetchQuotaStatus();
                        return;
                    }
                    console.error('Failed to submit feedback', err);
                    this.toastError('Submission failed', 'Unable to submit feedback at this time. Please try again.');
                } finally {
                    this.isSubmitting = false;
                }
            },
            async submitProblemReport() {
                if (!this.isUnlimitedProblem && !this.quota.canSubmitProblem) {
                    this.toastWarning('Daily Limit', this.quota.problemMessage || ("You've submitted " + this.quota.problemDailyLimit + " problem reports today. Our team is already looking into things. Please hold on and check back tomorrow 🙏"), 8);
                    return;
                }
                if (!this.problemTitle.trim() || !this.problemDescription.trim() || this.isSubmitting) return;
                this.isSubmitting = true;

                try {
                    const base64List = this.attachments.map(a => a.base64);
                    const payload = {
                        type: 'Problem',
                        starRating: null,
                        category: this.problemCategory,
                        title: this.problemTitle.trim(),
                        description: this.problemDescription.trim(),
                        diagnosticContextJson: this.includeTelemetry ? JSON.stringify(this.diagnosticContext) : null,
                        attachments: base64List,
                        attachmentBase64: base64List.length > 0 ? base64List[0] : null
                    };

                    const config = (this.isActualDevHost && this.currentEnvironment === 'production')
                        ? { params: { env: 'production' } }
                        : undefined;
                    const resp = await this.$http.post('/Feedback', payload, config);
                    if (resp.data && resp.data.gitHubIssueUrl) {
                        const issueNum = resp.data.gitHubIssueNumber ? ` #${resp.data.gitHubIssueNumber}` : '';
                        this.toastSuccess('Problem Logged to GitHub', `Bug report${issueNum} successfully dispatched to engineering team on GitHub.`);
                    } else {
                        this.toastSpecialThanks('problem');
                    }
                    await this.fetchQuotaStatus();

                    // Reset
                    this.problemTitle = '';
                    this.problemDescription = '';
                    this.attachments = [];
                    this.closeModal();
                } catch (err) {
                    if (err.status === 429) {
                        const rawMsg = err.message || ("You've submitted " + this.quota.problemDailyLimit + " problem reports today. Our team is already looking into things. Please hold on and check back tomorrow 🙏");
                        const polite = rawMsg.replace(/:\)/g, '😊');
                        this.toastWarning('Daily Limit', polite, 10);
                        this.fetchQuotaStatus();
                        return;
                    }
                    console.error('Failed to submit problem report', err);
                    this.toastError('Submission failed', 'Unable to submit problem report at this time. Please try again.');
                } finally {
                    this.isSubmitting = false;
                }
            }
        }
    };
</script>

<style scoped>
    .limit-reached-card {
        background-color: var(--vt-card-bg, #ffffff);
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .limit-reached-icon-circle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        font-size: 1.8rem;
    }

    .bg-light-info {
        background-color: rgba(13, 202, 240, 0.15);
    }

    .bg-light-warning {
        background-color: rgba(245, 158, 11, 0.15);
    }

    .feedback-tab-btn {
        background-color: transparent;
        border: 1px solid transparent;
        color: var(--vt-muted, #6c757d);
        font-weight: 600;
        padding: 6px 14px;
        border-radius: 20px;
        transition: all 0.2s ease;
    }

    .feedback-tab-btn:hover {
        color: var(--vt-text, #212529);
        background-color: rgba(0, 0, 0, 0.05);
    }

    .feedback-tab-btn.active {
        color: #ffffff;
        background-color: #0062cc;
        border-color: #0062cc;
    }

    .star-rating-box {
        background-color: rgba(0, 0, 0, 0.02);
        border: 1px dashed var(--vt-border, #dee2e6);
    }

    .stars-row {
        display: flex;
        justify-content: center;
        gap: 12px;
    }

    .star-btn {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-size: 2.2rem;
        color: #cbd5e1;
        transition: transform 0.15s ease, color 0.15s ease;
        line-height: 1;
    }

    .star-btn:hover {
        transform: scale(1.18);
    }

    .star-btn.active {
        color: #f59e0b;
    }

    .rating-label {
        font-size: 0.95rem;
        min-height: 22px;
    }

    /* Category Ratings Breakdown */
    .category-ratings-list {
        background-color: rgba(0, 0, 0, 0.015);
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .category-rating-row {
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .category-rating-row:last-child {
        border-bottom: none;
    }

    .mini-stars-row {
        display: flex;
        gap: 4px;
    }

    .mini-star-btn {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-size: 1.15rem;
        color: #cbd5e1;
        transition: transform 0.1s ease, color 0.1s ease;
        line-height: 1;
    }

    .mini-star-btn:hover {
        transform: scale(1.2);
    }

    .mini-star-btn.active {
        color: #f59e0b;
    }

    .mini-rating-value {
        font-size: 0.72rem;
        padding: 2px 5px;
    }

    .reset-cat-btn {
        line-height: 1;
        font-size: 0.9rem;
    }

    .chip-btn {
        font-size: 0.82rem;
        padding: 6px 14px;
        cursor: pointer;
        border: 1px solid var(--vt-border, #dee2e6);
        transition: all 0.15s ease;
    }

    .chip-inactive {
        background-color: transparent;
        color: var(--vt-text, #495057);
    }

    .chip-inactive:hover {
        background-color: rgba(0, 0, 0, 0.05);
        color: var(--vt-text, #212529);
    }

    .chip-active-problem {
        background-color: #dc3545;
        color: #ffffff;
        border-color: #dc3545;
    }

    .attachment-dropzone {
        background-color: rgba(0, 0, 0, 0.02);
        border: 2px dashed var(--vt-border, #ced4da) !important;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .attachment-dropzone:hover,
    .dropzone-active {
        border-color: #0062cc !important;
        background-color: rgba(0, 98, 204, 0.04);
    }

    .attachment-card {
        background-color: rgba(0, 0, 0, 0.02);
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .preview-thumbnail {
        width: 48px;
        height: 38px;
        object-fit: cover;
        border: 1px solid var(--vt-border, #ced4da);
    }

    .diagnostic-card {
        background-color: rgba(0, 0, 0, 0.015);
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .diagnostic-header {
        background-color: rgba(0, 0, 0, 0.02);
        cursor: pointer;
    }

    .shortcut-table th,
    .shortcut-table td {
        vertical-align: middle;
        padding: 8px 12px;
    }

    .tip-card {
        background-color: rgba(0, 0, 0, 0.02);
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .module-badge-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        font-size: 0.9rem;
    }

    .bg-light-primary {
        background-color: rgba(13, 110, 253, 0.12);
    }

    .bg-light-success {
        background-color: rgba(25, 135, 84, 0.12);
    }

    .qa-card {
        background-color: rgba(0, 0, 0, 0.015);
        border-color: var(--vt-border, #dee2e6) !important;
        transition: background-color 0.15s ease;
    }

    .qa-card:hover {
        background-color: rgba(0, 0, 0, 0.035);
    }

    /* Dark Mode Nuances */
    [data-theme="dark"] .feedback-tab-btn:hover {
        color: #ffffff;
        background-color: rgba(255, 255, 255, 0.08);
    }

    [data-theme="dark"] .star-rating-box {
        background-color: rgba(255, 255, 255, 0.02);
        border-color: var(--vt-border, #2d3748);
    }

    [data-theme="dark"] .star-btn {
        color: #475569;
    }

    [data-theme="dark"] .star-btn.active {
        color: #f59e0b;
    }

    [data-theme="dark"] .category-ratings-list {
        background-color: rgba(255, 255, 255, 0.02);
        border-color: var(--vt-border, #2d3748) !important;
    }

    [data-theme="dark"] .category-rating-row {
        border-bottom-color: rgba(255, 255, 255, 0.06);
    }

    [data-theme="dark"] .mini-star-btn {
        color: #475569;
    }

    [data-theme="dark"] .mini-star-btn.active {
        color: #f59e0b;
    }

    [data-theme="dark"] .chip-inactive {
        background-color: rgba(255, 255, 255, 0.03);
        color: var(--vt-text, #e2e8f0);
        border-color: var(--vt-border, #2d3748);
    }

    [data-theme="dark"] .chip-inactive:hover {
        background-color: rgba(255, 255, 255, 0.08);
    }

    [data-theme="dark"] .attachment-dropzone {
        background-color: rgba(255, 255, 255, 0.02);
        border-color: var(--vt-border, #2d3748) !important;
    }

    [data-theme="dark"] .attachment-dropzone:hover,
    [data-theme="dark"] .dropzone-active {
        border-color: #3b82f6 !important;
        background-color: rgba(59, 130, 246, 0.08);
    }

    [data-theme="dark"] .attachment-card {
        background-color: rgba(255, 255, 255, 0.03);
        border-color: var(--vt-border, #2d3748) !important;
    }

    [data-theme="dark"] .preview-thumbnail {
        border-color: var(--vt-border, #2d3748);
    }

    [data-theme="dark"] .diagnostic-card,
    [data-theme="dark"] .tip-card,
    [data-theme="dark"] .qa-card {
        background-color: rgba(255, 255, 255, 0.02);
        border-color: var(--vt-border, #2d3748) !important;
    }

    [data-theme="dark"] .qa-card:hover {
        background-color: rgba(255, 255, 255, 0.04);
    }

    [data-theme="dark"] .diagnostic-header {
        background-color: rgba(255, 255, 255, 0.04);
    }

    [data-theme="dark"] .shortcut-table {
        color: var(--vt-text, #e2e8f0);
    }
</style>

<style>
    /* Ensure Feedback & Support Modal layers above welcome modal when previewed */
    .modal#feedback-support-modal {
        z-index: 1065 !important;
    }
</style>
